(function () {
  const DRAWER_ID = 'slidecarthq';
  const PANEL_SELECTOR = '.slidecarthq';
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const CUSTOM_CONTROL_SELECTORS = ['.sb__non-covered-link'];

  const FOCUS_COLOR = '#07BCDD';
  const FOCUS_OUTLINE = `2px solid ${FOCUS_COLOR}`;
  const FOCUS_SHADOW = `0 0 0 2px ${FOCUS_COLOR}`;

  let isOpen = false;
  let activePanel = null;
  let returnFocusEl = null;
  let trapHandler = null;
  let escapeHandler = null;
  let focusedEl = null;

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function getPanel(drawer) {
    return drawer?.querySelector(PANEL_SELECTOR) || null;
  }

  function isFocusableVisible(el) {
    if (!el || el.disabled) return false;

    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity) === 0) return false;

    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function isExcludedFromTabOrder(el, panel) {
    if (!el || !panel) return true;
    if (el.closest('.slidecarthq-overlay')) return true;

    let node = el;
    while (node && node !== panel) {
      if (node.getAttribute('aria-hidden') === 'true') return true;
      if (node.classList?.contains('slick-cloned')) return true;

      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return true;

      node = node.parentElement;
    }

    return false;
  }

  function getFocusableElements(panel) {
    if (!panel) return [];

    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
      return isFocusableVisible(el) && !isExcludedFromTabOrder(el, panel);
    });
  }

  function onActivateKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.currentTarget.click();
  }

  function enhanceCustomControls(panel) {
    if (!panel) return;

    CUSTOM_CONTROL_SELECTORS.forEach((selector) => {
      panel.querySelectorAll(selector).forEach((el) => {
        if (el.dataset.pmAmpEnhanced === 'true' || !isFocusableVisible(el)) return;

        el.dataset.pmAmpEnhanced = 'true';
        el.setAttribute('tabindex', '0');
        if (!el.getAttribute('role')) {
          el.setAttribute('role', 'link');
        }

        const label = el.textContent?.replace(/\s+/g, ' ').trim();
        if (label && !el.getAttribute('aria-label')) {
          el.setAttribute('aria-label', label);
        }

        el.addEventListener('keydown', onActivateKeydown);
      });
    });
  }

  function scheduleCustomControlEnhancement(panel) {
    enhanceCustomControls(panel);
    window.setTimeout(() => enhanceCustomControls(panel), 300);
    window.setTimeout(() => enhanceCustomControls(panel), 800);
  }

  function keepOverlayOutOfTabOrder(drawer) {
    const overlay = drawer?.querySelector('.slidecarthq-overlay');
    if (overlay) overlay.setAttribute('tabindex', '-1');
  }

  function isFormControl(el) {
    return el.matches('input, select, textarea');
  }

  function applyFocusOutline(el) {
    if (!el) return;

    if (el.dataset.pmAmpStored !== 'true') {
      el.dataset.pmAmpOutline = el.style.outline || '';
      el.dataset.pmAmpOutlineOffset = el.style.outlineOffset || '';
      el.dataset.pmAmpBoxShadow = el.style.boxShadow || '';
      el.dataset.pmAmpStored = 'true';
    }

    el.style.setProperty('outline', FOCUS_OUTLINE, 'important');
    el.style.setProperty('outline-offset', isFormControl(el) ? '0px' : '2px', 'important');
    el.style.setProperty('box-shadow', FOCUS_SHADOW, 'important');
  }

  function removeFocusOutline(el) {
    if (!el || el.dataset.pmAmpStored !== 'true') return;

    el.style.outline = el.dataset.pmAmpOutline || '';
    el.style.outlineOffset = el.dataset.pmAmpOutlineOffset || '';
    el.style.boxShadow = el.dataset.pmAmpBoxShadow || '';

    delete el.dataset.pmAmpStored;
    delete el.dataset.pmAmpOutline;
    delete el.dataset.pmAmpOutlineOffset;
    delete el.dataset.pmAmpBoxShadow;
  }

  function clearFocusOutline() {
    if (focusedEl) {
      removeFocusOutline(focusedEl);
      focusedEl = null;
    }
  }

  function bindFocusIndicators(panel) {
    if (!panel || panel.dataset.pmAmpFocusBound === 'true') return;
    panel.dataset.pmAmpFocusBound = 'true';

    panel.addEventListener(
      'focusin',
      (event) => {
        if (!panel.contains(event.target)) return;
        clearFocusOutline();
        focusedEl = event.target;
        applyFocusOutline(event.target);
      },
      true
    );

    panel.addEventListener(
      'focusout',
      (event) => {
        if (focusedEl === event.target) {
          removeFocusOutline(event.target);
          focusedEl = null;
        }
      },
      true
    );
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !activePanel) return;

    const focusable = getFocusableElements(activePanel);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindKeyboardHandlers() {
    unbindKeyboardHandlers();

    trapHandler = trapFocus;
    escapeHandler = (event) => {
      if (event.key !== 'Escape' || !isOpen) return;
      event.preventDefault();
      if (typeof window.SLIDECART_CLOSE === 'function') {
        window.SLIDECART_CLOSE();
      }
    };

    document.addEventListener('keydown', trapHandler, true);
    document.addEventListener('keydown', escapeHandler, true);
  }

  function unbindKeyboardHandlers() {
    if (trapHandler) {
      document.removeEventListener('keydown', trapHandler, true);
      trapHandler = null;
    }
    if (escapeHandler) {
      document.removeEventListener('keydown', escapeHandler, true);
      escapeHandler = null;
    }
  }

  function activateTrap() {
    const drawer = getDrawer();
    const panel = getPanel(drawer);
    if (!panel) return;

    keepOverlayOutOfTabOrder(drawer);
    scheduleCustomControlEnhancement(panel);
    bindFocusIndicators(panel);
    bindKeyboardHandlers();

    activePanel = panel;
    isOpen = true;

    window.setTimeout(() => {
      if (!isOpen || activePanel !== panel) return;
      const closeButton = panel.querySelector('header button[aria-label*="close" i]');
      if (closeButton) closeButton.focus();
    }, 150);
  }

  function deactivateTrap() {
    if (!isOpen) return;

    isOpen = false;
    unbindKeyboardHandlers();

    clearFocusOutline();
    activePanel = null;

    if (returnFocusEl && isFocusableVisible(returnFocusEl)) {
      returnFocusEl.focus();
    }
    returnFocusEl = null;
  }

  function chainCallback(name, handler) {
    const previous = window[name];
    window[name] = function chainedAmpCartCallback(...args) {
      if (typeof previous === 'function') {
        previous.apply(this, args);
      }
      handler.apply(this, args);
    };
  }

  function init() {
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('[data-pmd-cart-open], [data-cart-drawer-toggle], .pm-nav-cart, .pm-product-atc');
        if (trigger) returnFocusEl = trigger;
      },
      true
    );

    chainCallback('SLIDECART_OPENED', activateTrap);
    chainCallback('SLIDECART_CLOSED', deactivateTrap);

    chainCallback('SLIDECART_ADD_TO_CART', function onSlideCartAddToCart() {
      if (document.activeElement && document.activeElement !== document.body) {
        returnFocusEl = document.activeElement;
      }
    });

    chainCallback('SLIDECART_UPDATED', function onSlideCartUpdated() {
      if (!isOpen) return;
      enhanceCustomControls(getPanel(getDrawer()));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
